/**
 * Order menu catalog query: GET menu_items. Used by Order tab only.
 */
import { useQuery } from '@tanstack/react-query';
import { fetchOrderMenuItems, type OrderMenuCategory, type ShopSettings } from './api';
import { isOnlineOrdersFeatureEnabled } from '../config';
import { orderMenuQueryKey } from './keys';

const DEFAULT_ITEM_PRICE = 0.5;

export interface OrderMenuCartItem {
  id: number;
  name: string;
  description?: string;
  attachment?: string;
  image?: string;
  price: number;
  quantity: number;
  a_name?: string;
  a_description?: string;
}

function flattenOrderMenu(categories: OrderMenuCategory[]): OrderMenuCartItem[] {
  const out: OrderMenuCartItem[] = [];
  const seen = new Set<number>();
  const list = categories ?? [];
  for (const cat of list) {
    const typeData = cat.type_data ?? cat.items ?? [];
    for (const it of typeData) {
      const id = it.id ?? (it as any).menu_item_id;
      if (id == null || seen.has(id)) {continue;}
      seen.add(id);
      const price =
        typeof it.price === 'number' ? it.price : it.cost != null ? Number(it.cost) : DEFAULT_ITEM_PRICE;
      out.push({
        id,
        name: it.name ?? (it as any).title ?? 'Item',
        a_name: it.a_name,
        description: it.description,
        a_description: it.a_description,
        attachment: it.attachment,
        image: it.attachment ?? (it as any).image,
        price,
        quantity: 0,
      });
    }
  }
  return out;
}

export function useOrderMenuQuery(options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? isOnlineOrdersFeatureEnabled();
  const query = useQuery({
    queryKey: orderMenuQueryKey,
    queryFn: fetchOrderMenuItems,
    staleTime: 1000 * 60 * 5,
    enabled,
  });

  const raw = query.data;
  const categories = raw?.menu_items ?? raw?.menuItems ?? [];
  const shopSettings: ShopSettings | null =
    raw?.shopSettings ?? raw?.configurations?.shopSettings ?? null;

  const allItems = flattenOrderMenu(categories);

  return {
    data: {
      allItems,
      categories,
      shopSettings,
    },
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
